/**
 * Shapes shared by the authoring operations in this directory.
 *
 * An operation that policy can stop returns a {@link Refusal} instead of
 * throwing: every call driven by an editor gesture or a tool call can be
 * refused, and a returned value makes the caller narrow before it reads the
 * result. Faults (a load failure, a bug) still throw, after the operation has
 * taken back what it had done.
 *
 * An operation never pushes to the undo stack, selects, or enters or leaves
 * play mode. It returns the command for what it did, already applied, and the
 * host pushes it or bundles it into a larger step.
 */

import type { SceneObject } from '../../core/SceneObject';
import type { UnpackFolderTarget } from '../../core/TraceScene';
import type { PageEditing } from '../states/pageCapture';
import type { UndoCommand } from '../undo/UndoCommand';

/**
 * Why an operation stopped. The scene is as it was; files a copy had already
 * uploaded stay in the host's folder.
 *
 * - `PLAY_MODE`: the scene is playing, and play writes nothing that survives
 *   Stop. The host may leave play mode and ask again.
 * - `UNDO_IN_PROGRESS`: an undo or redo is still running
 * - `DISPOSED`: an object the operation was handed has been deleted, or a
 *   behavior it was handed has been removed from its object
 * - `STRUCTURAL_ROOT`: the object is the document root or the engine root
 * - `EDITOR_ROOT`: the object is the editor root, which sits in the scene
 *   frame; what is inside it moves instead
 * - `SCENE_ANCHOR`: the object carries the scene's `ImageAnchor`, which a
 *   location-based scene needs
 * - `CYCLE`: the object would become its own ancestor
 * - `NO_FOLDER`: the edit lands inside a placed reference, and the host gave
 *   no folder to copy the reference's files into
 * - `READ_ONLY`: the host holds the object read-only (`AuthoringPorts.readOnly`)
 * - `REPARENT_REFUSED`: the scene refused a move: the parent is, or is inside,
 *   a scene reference, or a reparent policy vetoed it, or vetoed the unpack a
 *   copy needs
 * - `STATE_ROUTED`: a keyframe is selected on the object or an ancestor, and
 *   the edit would land on Base, out of sight; or, for a pose or visibility,
 *   the object is a page's shared content showing the state that page
 *   assigns, and the host has page editing off (`pageName` names the page)
 * - `LAYOUT_OWNED`: a layout writes the object's position or scale, and its
 *   next solve would undo the edit
 * - `BINDING_OWNED`: Base binds the pose component the edit changes to a
 *   property, which writes it again whenever the property changes; or an
 *   opacity edit in Default would move the opacity onto an `opacity`
 *   property the object declares for something else
 * - `INVALID_MATE`: a mate names an anchor an object lacks, or joins an
 *   object to itself or to a node under it
 * - `INVALID_SLOT`: the object, or the subtree it passes the slot to, has no
 *   material slot of that number, or none yet (a model still loading), or
 *   the slot is a Text object's, which takes its color from its Text
 * - `INVALID_WIRING`: an interaction or clock names something its target
 *   lacks: a state, a declared property of the right type, a child, or media
 * - `EVENT_CONFLICT`: the object's graph already handles the event an
 *   interaction or clock appends
 */
export type RefusalCode =
    | 'PLAY_MODE' | 'UNDO_IN_PROGRESS' | 'DISPOSED' | 'STRUCTURAL_ROOT' | 'EDITOR_ROOT' | 'SCENE_ANCHOR' | 'CYCLE'
    | 'NO_FOLDER' | 'READ_ONLY' | 'REPARENT_REFUSED' | 'STATE_ROUTED' | 'LAYOUT_OWNED' | 'BINDING_OWNED' | 'INVALID_MATE' | 'INVALID_SLOT'
    | 'INVALID_WIRING' | 'EVENT_CONFLICT';

export interface Refusal {
    code: RefusalCode;
    message: string;
    /** The object the refusal is about, when there is one. */
    object?: SceneObject;
    /**
     * On a `STATE_ROUTED` refusal caused by a page, the showing page whose
     * own state the edit would change. Absent when a selected keyframe
     * caused it.
     */
    pageName?: string;
}

/** The refusal for an edit asked for while the scene is in play mode. */
export const PLAY_MODE_REFUSAL: Readonly<Refusal> = Object.freeze({
    code: 'PLAY_MODE',
    message: 'The scene is in play mode, and an edit made now would not survive Stop',
});

/** The refusal for an edit asked for while an undo or redo replays. */
export const UNDO_IN_PROGRESS_REFUSAL: Readonly<Refusal> = Object.freeze({
    code: 'UNDO_IN_PROGRESS',
    message: 'An undo or redo is still running',
});

/** An operation's outcome: what it did and its one undo step, or why it did nothing. */
export type OpResult<T> = (T & { command: UndoCommand }) | { refused: Refusal };

/**
 * A batch entry's refusal. `entry`, the entry's index in the batch, is the key:
 * `object` is what the refusal is about, which can be another entry's object
 * when one entry's refusal stops the batch.
 */
export interface BatchRefusal extends Refusal {
    entry: number;
}

/**
 * A batch's outcome. The entries that pass their checks are applied as one
 * undo step; each entry that does not is refused on its own. Both lists keep
 * the batch's order. `command` is null when nothing was applied.
 */
export interface BatchResult<T> {
    applied: T[];
    refused: BatchRefusal[];
    command: UndoCommand | null;
}

/** What only the host can supply to an operation. */
export interface AuthoringPorts {
    /**
     * Where a copy-on-edit puts the files of the reference it copies: the
     * open scene's folder. Without it, an edit inside a placed reference is
     * refused with `NO_FOLDER`.
     */
    folder?: UnpackFolderTarget;
    /**
     * Whether the host holds `object` read-only, such as a node inside a
     * placed reference that the session's export would drop. An operation
     * refuses an edit to it with `READ_ONLY` before changing anything.
     */
    readOnly?(object: SceneObject): boolean;
    /**
     * Whether page editing is on for a Pages container. While it is and one of
     * its pages shows, a pose, visibility, or a slot's roughness, metallic or
     * opacity set on its shared content is that page's
     * (`pageCaptureContext`). Without it, the edit is Base's or a state's.
     */
    pageEditing?: PageEditing;
}
